import { useEffect, useState } from "react";
import HitoSheet from "./HitoSheet";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import { HitoBadge, SeverityChip } from "./HitoBadge";

/** Detalle de un evento (feedback de Carlos, 20 oct 2026): desde Eventos y desde el
 *  Panel, cada fila se abre y cuenta lo mismo que el aviso — qué ha pasado, por qué
 *  importa, la fuente y las fechas — sin tener que pasar por la ficha del producto. */
export type EventLike = {
  id: string; change_type: string; cn_code: string | null; product_name: string | null;
  active_ingredient?: string | null; laboratory?: string | null; detected_at: string | null;
  is_portfolio: boolean; is_competitor: boolean;
  hito_id: string | null; hito_name: string | null; hito_group: number | null; hito_group_name?: string | null;
  severity: "A" | "D" | "I" | null;
  extra_data?: { url?: string; fecha?: string; [k: string]: unknown } | null;
  source_code?: string | null; source_name?: string | null; source_since?: string | null;
  meaning?: string | null;
  detalle?: string | null; titulo?: string | null; explicacion?: string | null;
  antes?: string | null; despues?: string | null; source_url?: string | null;
  confirmations?: number; confirmed_by?: string[];
};

export default function EventDetailModal({ event, onClose, onOpenProduct }: {
  event: EventLike; onClose: () => void; onOpenProduct?: (cn: string) => void;
}) {
  const t = useT();
  const fmt = useFmt();
  const [sheet, setSheet] = useState(false);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const published = typeof event.extra_data?.fecha === "string" ? event.extra_data.fecha : null;
  const url = event.source_url || (typeof event.extra_data?.url === "string" ? event.extra_data.url : null);
  const row = (label: string, value: React.ReactNode) => value ? (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10, padding: "7px 0", borderTop: "1px solid var(--clr-border2)" }}>
      <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
      <span style={{ fontSize: "0.84rem", color: "var(--clr-text)", lineHeight: 1.45 }}>{value}</span>
    </div>
  ) : null;
  const relation = event.is_portfolio ? t("events.relationOwn") : event.is_competitor ? t("events.relationCompetitor") : t("events.relationMarket");

  return (
    <div role="dialog" aria-modal="true" aria-label={t("events.detailTitle")} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.4rem 1.5rem", width: 620, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)", color: "var(--clr-text)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8 }}>
              {event.hito_group ? t(`hitos.group${event.hito_group}`) : t("events.detailTitle")}
            </div>
            <h3 style={{ margin: "4px 0 0", fontSize: "1.05rem", fontWeight: 700, letterSpacing: "-0.01em" }}>
              {event.titulo || event.hito_name || t("events.detailTitle")}
            </h3>
          </div>
          <button onClick={onClose} aria-label={t("events.close")}
            style={{ background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999, width: 32, height: 32, cursor: "pointer", color: "var(--clr-text3)", flexShrink: 0 }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
          <HitoBadge c={event} t={t} showSeverity={false} maxWidth={360} />
          {event.severity && <SeverityChip severity={event.severity} t={t} />}
          <span style={{ fontSize: "0.72rem", color: "var(--clr-text3)" }}>{relation}</span>
        </div>

        {row(t("events.product"), event.product_name ? (
          event.cn_code && onOpenProduct
            ? <button onClick={() => onOpenProduct(event.cn_code!)} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--clr-org-primary)", fontWeight: 600, fontSize: "inherit", textAlign: "left", fontFamily: "inherit" }}>
                {event.product_name} ↗
              </button>
            : event.product_name
        ) : null)}
        {row(t("events.ingredientLab"), [event.active_ingredient, event.laboratory].filter(Boolean).join(" · ") || null)}
        {row(t("events.whatHappened"), (
          <>
            {event.detalle && <div style={{ fontWeight: 600 }}>{event.detalle}</div>}
            {event.explicacion && event.explicacion !== event.detalle && <div style={{ color: "var(--clr-text2)", marginTop: event.detalle ? 4 : 0 }}>{event.explicacion}</div>}
            {!event.detalle && !event.explicacion && (event.titulo || event.hito_name)}
          </>
        ))}
        {row(t("events.whyItMatters"), event.meaning || null)}
        {row(t("events.source"), (event.source_name || url) ? (
          <span>
            {event.source_name}
            {url && <> {event.source_name ? "· " : ""}<a href={url} target="_blank" rel="noreferrer" style={{ color: "var(--clr-org-primary)" }}>{t("events.openDocument")} ↗</a></>}
            {event.confirmations && event.confirmations > 1 ? <span style={{ color: "var(--clr-text3)" }}> · {t("events.confirmedBy", { n: event.confirmations })}</span> : null}
            {event.source_since && <div style={{ fontSize: "0.74rem", color: "var(--clr-text3)", marginTop: 2 }}>{t("events.sourceSince", { date: fmt.date(new Date(event.source_since)) })}</div>}
          </span>
        ) : null)}
        {row(t("events.published"), published ? fmt.date(new Date(published.length === 10 ? published + "T12:00:00" : published)) : null)}
        {row(t("events.detected"), event.detected_at ? fmt.date(new Date(event.detected_at), { timeZone: "UTC" }) : null)}
        {row(/^\d{6}$/.test(event.cn_code || "") ? "CN" : t("events.nregistro"), event.cn_code)}
        {event.hito_id && (
          <button onClick={() => setSheet(true)} style={{ marginTop: 10, background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--clr-org-primary)", fontSize: "0.8rem", fontWeight: 600 }}>
            {t("hitos.sheetOpen")} →
          </button>
        )}
        {sheet && event.hito_id && <HitoSheet hitoId={event.hito_id} onClose={() => setSheet(false)} />}
      </div>
    </div>
  );
}
